import { yupResolver } from '@hookform/resolvers/yup';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { Checkbox } from '@/components/form/Checkbox/Checkbox';
import { InputItem } from '@/components/form/Input/InputItem';
import { SelectItem } from '@/components/form/Select/SelectItem';
import { TermsConditionsAndPrivacyPolicyLink } from '@/components/form/TermsConditionsAndPrivacyPolicyLink/TermsConditionsAndPrivacyPolicyLink';
import { TextareaItem } from '@/components/form/Textarea/TextareaItem';
import { Label } from '@/components/form/shared/Label';
import useNearestLocation from '@/hooks/useNearestLocation';
import { LocationDto, SimpleLocationDto } from '@/lib/locations/dto';
import { SEATS_OPTIONS } from '@/utils/constants';
import { getIndustryOptions } from '@/utils/industries';
import { mapLocationsToOptions } from '@/utils/locations';

import { ToggleTourTypeOption } from './ToggleTourTypeOption';
import {
  ApplicationFormData,
  CalendlyLinkByTour,
  TourType,
  applicationFormSchema,
} from './types';
import { canSelectTourType, getAction } from './utils';

import clsx from 'clsx';

type ApplicationFormProps = {
  showLocationSelection: boolean;
  allLocations?: SimpleLocationDto[];
  location?: SimpleLocationDto | LocationDto | null;
  selectedTourType: TourType;
  isLoading: boolean;
  onTourTypeChange: (t: TourType) => void;
  onSubmit: (data: ApplicationFormData) => void;
  onChange: (data: ApplicationFormData) => void;
  onLocationChange: (sfId: string) => void;
  availableTourTypes?: CalendlyLinkByTour[] | null;
};

export default function ApplicationForm({
  showLocationSelection,
  allLocations,
  onLocationChange,
  location,
  selectedTourType,
  onTourTypeChange,
  onSubmit,
  onChange,
  isLoading,
  availableTourTypes,
}: ApplicationFormProps) {
  const t = useTranslations('location-form.form');

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm<ApplicationFormData>({
    resolver: yupResolver(applicationFormSchema),
    mode: 'onBlur',
    defaultValues: {
      location: location?.sfId,
      industry: '',
      teamSize: '',
      textMessageOptIn: false,
    },
  });

  const watchAllFields = watch();
  const locale = useLocale();

  const isTourTypeSelectable =
    location &&
    availableTourTypes &&
    availableTourTypes.length > 0 &&
    canSelectTourType(getAction(location));

  const locationOptions = allLocations
    ? mapLocationsToOptions(allLocations)
    : [];

  useNearestLocation({
    locale,
    disabled: !showLocationSelection,
    onChange: async (nearestLocation) => {
      if (!nearestLocation) return;

      setValue('location', nearestLocation.sfId);
      onLocationChange(nearestLocation.sfId);
    },
  });

  useEffect(() => {
    onChange(getValues());
  }, [watchAllFields]);

  const industryOptions = getIndustryOptions(locale as 'fr' | 'en' | 'nl');

  return (
    <form
      className="flex flex-col"
      onSubmit={handleSubmit((data) => {
        onSubmit(data);
      })}
    >
      <div
        className={`flex flex-col${showLocationSelection ? '-reverse' : ''}`}
      >
        {isTourTypeSelectable && (
          <div className="mb-6">
            <div>
              <Label required className="mb-2" data-testid="label-tour-type">
                {t('tourType.label')}
              </Label>
              {availableTourTypes.map(({ type }) => (
                <ToggleTourTypeOption
                  key={type}
                  active={selectedTourType === type}
                  onClick={() => onTourTypeChange(type)}
                  data-testid={`btn-tour-type-${type}`}
                >
                  {t(`tourType.${type}`)}
                </ToggleTourTypeOption>
              ))}
            </div>
            {selectedTourType === TourType.IN_PERSON &&
              location.salesOfficeAddress != null && (
                <p>
                  {t('sales-office-address', {
                    address: location.salesOfficeAddress,
                  })}
                </p>
              )}
          </div>
        )}
        <div className="flex flex-col gap-3 mb-7 sm:mb-5 sm:grid sm:grid-cols-4">
          <InputItem
            className="col-span-2"
            label={t('firstName')}
            {...register('firstName')}
            error={errors.firstName && t(errors.firstName.message)}
            data-testid="input-firstname"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('lastName')}
            {...register('lastName')}
            error={errors.lastName && t(errors.lastName.message)}
            data-testid="input-lastname"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('email')}
            {...register('email')}
            error={errors.email && t(errors.email.message)}
            data-testid="input-email"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('telephone')}
            {...register('phone')}
            error={errors.phone && t(errors.phone.message)}
            data-testid="input-telephone"
            required
          />
          <InputItem
            className="col-span-2"
            label={t('company')}
            {...register('company')}
            error={errors.company && t(errors.company.message)}
            data-testid="input-company"
            required
          />
          <div className="col-span-2" id="select-industry">
            <SelectItem
              label={t('industry')}
              options={industryOptions}
              {...register('industry')}
              data-testid="select-industry"
              required
            />
          </div>
          {showLocationSelection && locationOptions.length > 0 && (
            <div className="col-span-2">
              <SelectItem
                label={t('location')}
                options={locationOptions}
                {...register('location')}
                data-testid="select-location"
                onChange={(e) => onLocationChange(e.target.value)}
                required
              />
            </div>
          )}
          <div className={clsx(!showLocationSelection && 'col-span-2')}>
            <SelectItem
              label={t('teamSize')}
              options={SEATS_OPTIONS}
              {...register('teamSize')}
              data-testid="select-seats"
              required
            />
          </div>
          <InputItem
            className={clsx(!showLocationSelection && 'col-span-2')}
            label={t('referenceCode')}
            {...register('referenceCode')}
            error={errors.referenceCode && t(errors.referenceCode.message)}
            data-testid="input-referenceCode"
          />
          <TextareaItem
            className="col-span-4"
            label={t('comments')}
            {...register('comments')}
            error={errors.comments && t(errors.comments.message)}
            data-testid="input-comments"
          />
        </div>
      </div>
      <div className="mb-7 sm:mb-5">
        <Checkbox
          id="textMessageOptIn"
          {...register('textMessageOptIn')}
          className="text-sm"
          data-testid="sms-alert-checkbox"
          label={t('textMessageOptIn')}
        />
      </div>
      <TermsConditionsAndPrivacyPolicyLink />
      <PrimaryButton
        className="self-end w-full mt-8 sm:w-fit"
        type="submit"
        data-testid="submit-application-form"
        disabled={!isValid}
        isLoading={isLoading}
      >
        {t('submit')}
      </PrimaryButton>
    </form>
  );
}
